import { BaseDataListResponse } from 'Base'
import { ProjectTag } from 'Models'
import axiosInstant from 'api/baseRequest'
import { action, makeObservable, observable, runInAction } from 'mobx'
import { DEFAULT_PAGE_TABLE_SIZE } from 'utils/constants/commons-constant'
import { toast } from 'utils/toast'
import RootStore from '../RootStore'
import i18n from 'i18n'

class TagManagementStore {
  rootStore: RootStore
  loadingListing: boolean = false
  loadingSubmit: boolean = false
  loadingDetail: boolean = false
  filterData = {
    page: 1,
    perPage: DEFAULT_PAGE_TABLE_SIZE,
    keyword: '',
  }
  listData: BaseDataListResponse<ProjectTag> = {
    meta: {
      perPage: DEFAULT_PAGE_TABLE_SIZE,
      page: 1,
      total: 0,
    },
    data: [],
  }

  selectedItem: ProjectTag | null = null

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore

    makeObservable(this, {
      filterData: observable,
      loadingListing: observable,
      listData: observable,
      selectedItem: observable,
      loadingDetail: observable,
      loadingSubmit: observable,

      handleFilterDataChange: action,
      create: action,
      fetchDetail: action,
      fetchList: action,
      update: action,
      deleteItem: action,
      setSelectedItem: action,
    })
  }

  handleFilterDataChange = (filterData: any) => {
    this.filterData = {
      ...this.filterData,
      ...filterData,
    }
    this.fetchList()
  }

  setSelectedItem = (item: ProjectTag | null) => {
    this.selectedItem = item
  }

  fetchList = async () => {
    this.loadingListing = true
    try {
      const { data } = await axiosInstant.request({
        url: '/api/v1/tags',
        params: this.filterData,
      })
      runInAction(() => {
        this.listData = data
        if (data?.meta) {
          this.filterData = {
            ...this.filterData,
            page: data.meta.page,
            perPage: data.meta.perPage,
          }
        }
      })
    } catch (err) {
      console.error(err)
    } finally {
      runInAction(() => {
        this.loadingListing = false
      })
    }
  }

  fetchDetail = async (id: string) => {
    this.loadingDetail = true
    try {
      const { data } = await axiosInstant.request({
        url: `/api/v1/tags/${id}`,
      })
      runInAction(() => {
        this.selectedItem = data?.data || data
      })
    } catch (err) {
      console.error(err)
    } finally {
      runInAction(() => {
        this.loadingDetail = false
      })
    }
  }

  create = async (payload: { name: string }) => {
    this.loadingSubmit = true
    try {
      const res = await axiosInstant.request({
        url: `/api/v1/tags`,
        method: 'post',
        data: payload,
        hideToast: true,
      })
      toast({
        severity: 'success',
        detail: i18n.t('Created item', { item: i18n.t('Tag') }),
        summary: i18n.t('Success'),
      })
      await this.fetchList()
      return res
    } catch (err) {
      throw err
    } finally {
      runInAction(() => {
        this.loadingSubmit = false
      })
    }
  }

  update = async (id: string, payload: { name: string }) => {
    this.loadingSubmit = true
    try {
      const res = await axiosInstant.request({
        url: `/api/v1/tags/${id}`,
        method: 'put',
        data: payload,
      })
      toast({
        severity: 'success',
        detail: i18n.t('Updated item', { item: i18n.t('Tag') }),
        summary: i18n.t('Success'),
      })
      await this.fetchList()
      return res
    } catch (err) {
      throw err
    } finally {
      runInAction(() => {
        this.loadingSubmit = false
      })
    }
  }

  deleteItem = async (id: string) => {
    this.loadingListing = true
    try {
      await axiosInstant.request({
        url: `/api/v1/tags/${id}`,
        method: 'delete',
      })
      await this.fetchList()
      toast({
        severity: 'success',
        detail: i18n.t('Deleted item', { item: i18n.t('Tag') }),
        summary: i18n.t('Success'),
      })
    } catch (err) {
      console.error(err)
    } finally {
      runInAction(() => {
        this.loadingListing = false
      })
    }
  }
}

export default TagManagementStore
